import {
  ArrowDownTrayIcon,
  Avatar,
  Button,
  CheckIcon,
  CubeTransparentIcon,
  Divider,
  Drawer,
  Flex,
  IconButton,
  Image,
  PlusIcon,
  Spacer,
  Text,
  toaster,
  XMarkIcon,
} from 'components'
import ColorModeSwitch from 'components/ColorModeSwitch'
import { ColorMode, useCurrentUser } from 'components/providers'
import { generatePath, useNavigate, useParams } from 'react-router'
import {
  BaseUrl,
  createDoc,
  isProduction,
  makeEmptyWorkspace,
  signOut,
  useEntevatePermission,
  useSwitchWorkspace,
  useTheme,
  useWorkspaceLogo,
  useWorkspaces,
} from 'utils'
import { useCurrentPage } from '../utils/pageUtils'
import { RoutePath } from '../utils/routing'
import { useSideMenuItems } from '../utils/sideMenuUtils'
import CreateNewButton from './CreateNewButton'
import EntevatePermissionToggle from './EntevatePermissionToggle'
import SideMenuButton from './SideMenuButton'

interface Props {
  open: boolean
  onClose: () => void
}

export default function SideMenuDrawer({ open, onClose }: Props) {
  const user = useCurrentUser()
  const [entevatePermission] = useEntevatePermission()
  const { workspaceId } = useParams()
  const { workspaces, refetch } = useWorkspaces('emp')
  const { theme } = useTheme()
  const switchWorkspace = useSwitchWorkspace()
  const currentPage = useCurrentPage()
  const currentPagePath = currentPage?.isChild ? currentPage.parentPath : currentPage?.path
  const navigate = useNavigate()
  const sideMenuItems = useSideMenuItems()
  const { hasWorkspaceLogo, workspaceLogoSrc } = useWorkspaceLogo()

  const goToManage = (workspaceId: string) =>
    navigate(generatePath(RoutePath.ManageWorkspace, { workspaceId }))
  const workspacesList = [
    ...((workspaces ?? []).length > 1 ? [{ id: 'all', title: 'All' }] : []),
    ...(workspaces ?? []),
  ]

  async function handleNewWorkspace() {
    const doc = await createDoc(user?.id!, 'Workspace', makeEmptyWorkspace('emp'))
    toaster.create({
      title: 'Workspace created',
      type: 'success',
      description: 'Navigating to new workspace',
    })
    refetch()
    goToManage(doc.id)
    onClose()
  }

  return (
    <Drawer
      colorMode={theme as ColorMode}
      placement="start"
      onClose={onClose}
      open={open}
      hideClose
      header={
        <Flex flex={1} justify="center" align="center" justifyContent="space-between">
          <IconButton
            variant="ghost"
            aria-label="close drawer"
            icon={<XMarkIcon />}
            onClick={onClose}
          />
          <Image alt="Logo" height="20px" objectFit="contain" src={workspaceLogoSrc} />
          <CreateNewButton size="sm" removeText px={2} borderRadius="sm" onCreate={onClose} />
        </Flex>
      }
    >
      <Flex width="100%" height="100%" gap={2} direction="column" pb={3}>
        <Flex direction="column">
          {sideMenuItems.map((item, index) => (
            <SideMenuButton key={index} buttonProps={{ py: 2 }} onNavigate={onClose} {...item} />
          ))}
        </Flex>
        <Divider />

        <Flex direction="column" gap={1} pl={4} py={1}>
          <Text textStyle="bodyXSmall">{workspaces?.length == 1 ? 'Workspace' : 'Workspaces'}</Text>
          {workspacesList.map(({ id, title }) => (
            <Flex key={id}>
              <Button
                leftIcon={<CheckIcon opacity={workspaceId === id ? 1 : 0} />}
                borderRadius="sm"
                flex={1}
                variant="ghost"
                justifyContent="flex-start"
                onClick={() => {
                  switchWorkspace(id, currentPagePath)
                  onClose()
                }}
              >
                <Text
                  textStyle="bodySmall"
                  fontStyle={id === 'all' ? 'italic' : undefined}
                  truncate
                  opacity={title ? 1 : 0.5}
                  as={title ? 'p' : 'i'}
                >
                  {title || 'Draft'}
                </Text>
              </Button>
            </Flex>
          ))}
          {entevatePermission === 'entevate' && (
            <Button
              leftIcon={<PlusIcon />}
              borderRadius="sm"
              variant="outline"
              onClick={handleNewWorkspace}
            >
              Create New
            </Button>
          )}
        </Flex>
        <Divider />

        <Flex
          direction="row"
          gap={2}
          align="center"
          px={4}
          py={1}
          onClick={() => {
            navigate(`/${workspaceId}/profile`)
            onClose()
          }}
        >
          <Avatar
            key={user?.photoUrl!}
            cursor="pointer"
            size="sm"
            name={user?.name!}
            src={user?.photoUrl!}
          />
          <Text textStyle={'bodySmall'}>{user?.name}</Text>
          <Spacer />
        </Flex>
        <Divider />

        {isProduction() && user?.isAdmin && (
          <>
            <EntevatePermissionToggle alignSelf="flex-start" buttonProps={{ px: 3 }} />
            <Button
              leftIcon={<CubeTransparentIcon type="outline" fontSize="sm" />}
              onClick={() => {
                window.location.href = `${BaseUrl['dev-web']}${document.location.pathname}`
              }}
              variant="ghost"
              justifyContent="flex-start"
            >
              Development Site
            </Button>
            <Divider />
          </>
        )}

        <Button
          leftIcon={<ArrowDownTrayIcon size="sm" style={{ transform: 'rotate(90deg)' }} />}
          variant="ghost"
          justifyContent="flex-start"
          onClick={() => signOut()}
        >
          Log Out
        </Button>
        <Divider />

        <Flex direction="row" justifyContent="space-between" align="center" pl={4}>
          <Text textStyle="bodySmall">Theme</Text>
          <ColorModeSwitch />
        </Flex>
        <Spacer />

        {hasWorkspaceLogo && (
          <Flex direction="row" justify="center">
            <Flex cursor="pointer" direction="column" align="flex-start">
              <Text fontSize="10px" opacity={0.7}>
                powered by
              </Text>
              <Image
                src={
                  theme === 'light'
                    ? '/common/momentify-logo-dark.svg'
                    : '/common/momentify-logo-light.svg'
                }
                height="16px"
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Drawer>
  )
}
